"use client";

import { useState } from "react";

const BASE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://forgeswap.fun";

const CRYPTO_EXPERIENCE_OPTIONS = [
  "Less than 1 year",
  "1-2 years",
  "2-5 years",
  "5+ years",
];

const ECOSYSTEM_OPTIONS = [
  "Solana",
  "Ethereum",
  "Base",
  "BSC",
  "Arbitrum",
  "Polygon",
  "Avalanche",
  "Other",
];

const initialForm = {
  xHandle: "",
  telegramHandle: "",
  discordHandle: "",
  email: "",
  country: "",
  xFollowers: "",
  cryptoExperience: "",
  ecosystems: [] as string[],
  hasHostedSpaces: false,
  avgListenersPerSpace: "",
  spacesPerWeek: "",
  promotionPitch: "",
  referredBy: "",
};

export default function AffiliatesPage() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = <K extends keyof typeof initialForm>(key: K, value: typeof initialForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const toggleEcosystem = (ecosystem: string) => {
    setForm((current) => ({
      ...current,
      ecosystems: current.ecosystems.includes(ecosystem)
        ? current.ecosystems.filter((e) => e !== ecosystem)
        : [...current.ecosystems, ecosystem],
    }));
  };

  const handleSubmit = async () => {
    // Validation: X handle required
    if (!form.xHandle.trim()) {
      setError("Please provide your X (Twitter) handle.");
      return;
    }

    // Validation: email required
    if (!form.email.trim()) {
      setError("Please provide your email address.");
      return;
    }

    // Validation: pitch 100-1000 chars
    const pitchLength = form.promotionPitch.trim().length;
    if (pitchLength < 100) {
      setError(`Your promotion pitch needs to be at least 100 characters. Currently: ${pitchLength} characters.`);
      return;
    }
    if (pitchLength > 1000) {
      setError(`Your promotion pitch is too long. Maximum 1000 characters. Currently: ${pitchLength} characters.`);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("/api/affiliates/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          xHandle: form.xHandle.trim(),
          telegramHandle: form.telegramHandle.trim(),
          discordHandle: form.discordHandle.trim(),
          email: form.email.trim(),
          country: form.country.trim(),
          xFollowers: form.xFollowers.trim(),
          cryptoExperience: form.cryptoExperience,
          ecosystems: form.ecosystems.join(", "),
          hasHostedSpaces: form.hasHostedSpaces,
          avgListenersPerSpace: form.avgListenersPerSpace.trim(),
          spacesPerWeek: form.spacesPerWeek.trim(),
          promotionPitch: form.promotionPitch.trim(),
          referredBy: form.referredBy.trim(),
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || "Something went wrong.");
        return;
      }

      setSubmitted(true);
      setForm(initialForm);
    } catch (e) {
      console.error(e);
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: "#111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 560, textAlign: "center", padding: 40, background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>🤝</div>
          <h1 style={{ color: "#f9cd4f", fontSize: 32, fontWeight: 700, marginBottom: 16 }}>Application Received!</h1>
          <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7, marginBottom: 8 }}>Thanks for applying to the ForgeSwap Affiliate Program.</p>
          <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7 }}>We review every application manually and will reach out via Telegram or Discord if you're approved.</p>
          <a href={BASE_URL} style={{ display: "inline-block", marginTop: 32, background: "#f9cd4f", color: "#111", padding: "12px 32px", borderRadius: 10, fontWeight: 700, textDecoration: "none" }}>Back to ForgeSwap</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#111", minHeight: "100vh", color: "#fff", fontFamily: "Inter, sans-serif", overflow: "hidden", maxWidth: "100vw" }}>
      <div style={{ background: "linear-gradient(135deg, #1a1a1a, #111)", borderBottom: "1px solid #222", padding: "48px 16px 32px" }}>
        <div style={{ maxWidth: 840, margin: "0 auto", textAlign: "center" }}>
          <h1 style={{ color: "#f9cd4f", fontSize: "clamp(28px, 6vw, 42px)", fontWeight: 800, marginBottom: 16 }}>ForgeSwap Affiliate Program</h1>
          <p style={{ color: "#ccc", fontSize: 18, lineHeight: 1.7, maxWidth: 680, margin: "0 auto" }}>Earn commissions by promoting ForgeSwap to your audience. Perfect for crypto influencers, KOLs, and community builders.</p>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 0", display: "grid" }}>
        <div style={{ background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, padding: 28, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div style={{ display: "grid", gap: 24 }}>
            <div>
              <h2 style={{ color: "#f9cd4f", fontSize: "clamp(24px, 4vw, 32px)", fontWeight: 800, margin: "0 0 12px" }}>Why Become an Affiliate?</h2>
              <p style={{ color: "#fff", fontSize: 16, lineHeight: 1.8, margin: 0, maxWidth: 880 }}>
                ForgeSwap makes it easy for anyone to swap tokens across 30+ chains with Apple Pay, Google Pay, or credit card. As an affiliate, you earn a percentage of all swap fees generated by users you refer.
              </p>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700, margin: 0 }}>Program Benefits</h3>
              <div style={{ display: "grid", gap: 10 }}>
                {[
                  "Earn recurring commissions on all referral volume",
                  "Custom referral link with tracking dashboard",
                  "Weekly payouts in crypto or stablecoins",
                  "Exclusive promo materials and early access to features",
                  "Priority support and direct line to the team",
                ].map((item) => (
                  <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "rgba(17,17,17,0.9)", border: "1px solid rgba(249,205,79,0.14)", borderRadius: 14, padding: "14px 16px" }}>
                    <span style={{ color: "#f9cd4f", fontSize: 18, lineHeight: 1, fontWeight: 700 }}>✓</span>
                    <span style={{ color: "#fff", fontSize: 15, lineHeight: 1.7 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 48px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 32 }}>
        <div style={{ background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, padding: 28, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <h2 style={{ color: "#f9cd4f", fontSize: 22, fontWeight: 700, margin: "0 0 8px" }}>Apply Now</h2>
          <p style={{ color: "#888", fontSize: 14, margin: "0 0 28px" }}>Tell us about yourself and how you plan to promote ForgeSwap. We review every application manually.</p>

          {error ? (
            <div style={{ background: "#ef444422", border: "1px solid #ef4444", color: "#ef4444", padding: "12px 16px", borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 16 }}>
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>X (Twitter) Handle *</label>
              <input
                value={form.xHandle}
                onChange={(e) => update("xHandle", e.target.value)}
                placeholder="@yourhandle"
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Email *</label>
              <input
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Telegram Handle (optional)</label>
              <input
                value={form.telegramHandle}
                onChange={(e) => update("telegramHandle", e.target.value)}
                placeholder="@yourhandle"
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Discord Handle (optional)</label>
              <input
                value={form.discordHandle}
                onChange={(e) => update("discordHandle", e.target.value)}
                placeholder="username#1234 or username"
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Country</label>
              <input
                value={form.country}
                onChange={(e) => update("country", e.target.value)}
                placeholder="e.g. United States"
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>X (Twitter) Followers</label>
              <input
                value={form.xFollowers}
                onChange={(e) => update("xFollowers", e.target.value)}
                placeholder="e.g. 5,000"
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Crypto Experience</label>
              <select
                value={form.cryptoExperience}
                onChange={(e) => update("cryptoExperience", e.target.value)}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14 }}
              >
                <option value="">Select...</option>
                {CRYPTO_EXPERIENCE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Active Ecosystems</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {ECOSYSTEM_OPTIONS.map((ecosystem) => (
                  <button
                    key={ecosystem}
                    type="button"
                    onClick={() => toggleEcosystem(ecosystem)}
                    style={{
                      background: form.ecosystems.includes(ecosystem) ? "#f9cd4f" : "#111",
                      color: form.ecosystems.includes(ecosystem) ? "#111" : "#fff",
                      border: "1px solid #333",
                      padding: "8px 14px",
                      borderRadius: 8,
                      fontSize: 13,
                      cursor: "pointer",
                      fontWeight: form.ecosystems.includes(ecosystem) ? 600 : 400,
                    }}
                  >
                    {ecosystem}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 12, padding: 16 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.hasHostedSpaces}
                  onChange={(e) => update("hasHostedSpaces", e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: "#f9cd4f" }}
                />
                <span style={{ color: "#fff", fontSize: 14 }}>I host Twitter Spaces regularly</span>
              </label>

              {form.hasHostedSpaces && (
                <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
                  <div>
                    <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Average listeners per Space</label>
                    <input
                      value={form.avgListenersPerSpace}
                      onChange={(e) => update("avgListenersPerSpace", e.target.value)}
                      placeholder="e.g. 50-100"
                      style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Spaces per week</label>
                    <input
                      value={form.spacesPerWeek}
                      onChange={(e) => update("spacesPerWeek", e.target.value)}
                      placeholder="e.g. 2-3"
                      style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>How will you promote ForgeSwap? *</label>
              <textarea
                value={form.promotionPitch}
                onChange={(e) => update("promotionPitch", e.target.value)}
                placeholder="Tell us about your audience, your content style, and how you plan to drive users to ForgeSwap. Be specific! (100-1000 characters)"
                rows={6}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box", resize: "vertical" }}
              />
              <p style={{ color: "#666", fontSize: 11, marginTop: 4 }}>{form.promotionPitch.length}/1000 characters (minimum 100)</p>
            </div>

            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Referred by (optional)</label>
              <input
                value={form.referredBy}
                onChange={(e) => update("referredBy", e.target.value)}
                placeholder="@handle or name of who referred you"
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            <button onClick={handleSubmit} disabled={submitting} style={{ background: "#f9cd4f", color: "#111", border: "none", padding: "12px 18px", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gap: 20 }}>
          <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 16, padding: 24 }}>
            <h3 style={{ color: "#f9cd4f", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>Who Should Apply?</h3>
            <div style={{ display: "grid", gap: 12 }}>
              {[
                "Crypto influencers and KOLs with engaged audiences",
                "Twitter Spaces hosts in the DeFi/crypto niche",
                "Community managers and moderators",
                "Content creators (YouTube, TikTok, podcasts)",
                "Newsletter writers covering crypto/DeFi",
              ].map((item) => (
                <div key={item} style={{ color: "#ccc", fontSize: 14, lineHeight: 1.7, background: "#111", border: "1px solid #222", borderRadius: 12, padding: 14 }}>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 16, padding: 24 }}>
            <h3 style={{ color: "#f9cd4f", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>Application Tips</h3>
            <div style={{ display: "grid", gap: 12 }}>
              {[
                "Be specific about your audience size and engagement",
                "Explain your content strategy and posting frequency",
                "Highlight any previous crypto affiliate experience",
                "Share examples of your best-performing content",
              ].map((item) => (
                <div key={item} style={{ color: "#ccc", fontSize: 14, lineHeight: 1.7, background: "#111", border: "1px solid #222", borderRadius: 12, padding: 14 }}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
